import React, { useState, useEffect } from 'react';
import Draggable from 'react-draggable';
import { Card, Flex, Title, Text, Button } from './UIComponents';
import theme from '../styles/theme';
import { useMobile } from '../contexts/MobileContext';

const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const GameStats = ({ players, playerCount, roomId }) => {
  const [isMinimized, setIsMinimized] = useState(false);
  const [duration, setDuration] = useState(0);
  const { isMobile, togglePanel, calculatePanelPosition } = useMobile();
  
  // Get panel position info
  const positionInfo = calculatePanelPosition('gameStats');

  // Initialize minimized state based on mobile
  useEffect(() => {
    if (isMobile) {
      setIsMinimized(true);
    }
  }, [isMobile]);

  useEffect(() => {
    // Update the context when minimized state changes
    if (togglePanel) {
      togglePanel('gameStats', !isMinimized);
    }
  }, [isMinimized]);

  useEffect(() => {
    const timer = setInterval(() => {
      setDuration(prev => prev + 1);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const floatingPanelStyle = {
    position: 'fixed',
    top: isMobile ? 'auto' : '20px',
    left: isMobile ? '0' : '20px',
    bottom: isMobile ? positionInfo.bottom : 'auto',
    zIndex: isMobile ? positionInfo.zIndex : 1000,
    minWidth: isMobile ? '100%' : '300px',
    maxWidth: isMobile ? '100%' : '300px',
    backgroundColor: theme.colors.card,
    borderRadius: isMobile 
      ? `${theme.borders.radius.lg} ${theme.borders.radius.lg} 0 0` 
      : theme.borders.radius.lg,
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
    border: `1px solid ${theme.colors.border}`,
    cursor: isMobile ? 'default' : 'move',
    userSelect: 'none',
    transition: 'bottom 0.3s ease'
  };

  const dragHandleStyle = {
    padding: theme.spacing.xs,
    borderBottom: `1px solid ${theme.colors.border}`,
    borderTopLeftRadius: theme.borders.radius.lg,
    borderTopRightRadius: theme.borders.radius.lg,
    background: `linear-gradient(to right, ${theme.colors.background}, ${theme.colors.card})`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: isMobile ? 'default' : 'move'
  };

  // Sort players by points in descending order
  const sortedPlayers = Object.entries(players)
    .map(([socketId, player]) => ({ ...player, socketId }))
    .sort((a, b) => b.points - a.points);

  return (
    <Draggable 
      handle=".drag-handle" 
      bounds="body"
      disabled={isMobile}
    >
      <div style={floatingPanelStyle}>
        <div className="drag-handle" style={dragHandleStyle}>
          <Title level={3} style={{ margin: 0, cursor: isMobile ? 'default' : 'move', paddingLeft: theme.spacing.sm }}>Game Stats</Title>
          <Button
            onClick={() => setIsMinimized(!isMinimized)}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              color: theme.colors.primary,
              padding: theme.spacing.xs,
              cursor: 'pointer',
              fontSize: '1.2rem',
              lineHeight: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {isMinimized ? '🔽' : '🔼'}
          </Button>
        </div>

        {!isMinimized && (
          <Card style={{ 
            borderTopLeftRadius: 0, 
            borderTopRightRadius: 0,
            cursor: 'default',
            padding: theme.spacing.md
          }}>
            <Flex direction="column" gap="sm">
              <Flex direction="row" gap="xs" wrap="wrap">
                <Text style={{
                  flex: '1 1 auto',
                  fontSize: '0.85rem',
                  color: theme.colors.textSecondary,
                  textAlign: 'center',
                  padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                  backgroundColor: `${theme.colors.background}66`,
                  borderRadius: theme.borders.radius.sm,
                  border: `1px solid ${theme.colors.border}22`,
                  fontWeight: '600',
                  minWidth: '120px'
                }}>
                  Room: {roomId || 'Loading...'}
                </Text>
                <Text style={{
                  flex: '1 1 auto',
                  fontSize: '0.85rem',
                  color: theme.colors.textSecondary,
                  textAlign: 'center',
                  padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                  backgroundColor: `${theme.colors.background}66`,
                  borderRadius: theme.borders.radius.sm,
                  border: `1px solid ${theme.colors.border}22`,
                  fontWeight: '600',
                  minWidth: '120px'
                }}>
                  Game Time: {formatTime(duration)}
                </Text>
                <Text style={{
                  flex: '1 1 auto',
                  fontSize: '0.85rem',
                  color: theme.colors.textSecondary,
                  textAlign: 'center',
                  padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                  backgroundColor: `${theme.colors.background}66`,
                  borderRadius: theme.borders.radius.sm,
                  border: `1px solid ${theme.colors.border}22`,
                  minWidth: '120px'
                }}>
                  {playerCount} Players Online
                </Text>
              </Flex>
              {sortedPlayers.map((player) => (
                <div 
                  key={player.socketId} 
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                    color: getCellColor(player.id),
                    fontSize: '0.85rem',
                    transition: 'all 0.2s ease',
                    borderRadius: theme.borders.radius.sm,
                    '&:hover': {
                      backgroundColor: `${theme.colors.background}33`
                    }
                  }}
                >
                  <span>{player.username || `P${player.id}`}</span>
                  <span style={{ fontWeight: '600' }}>{player.points}</span>
                </div>
              ))}
            </Flex>
          </Card>
        )}
      </div>
    </Draggable>
  );
};

// Get cell color function (copied from Grid component)
const getCellColor = (playerId) => {
  const colors = [
    theme.colors.card, // Empty cell
    '#FF4136', // Player 1 - Red
    '#2ECC40', // Player 2 - Green
    '#0074D9', // Player 3 - Blue
    '#FFDC00', // Player 4 - Yellow
    '#B10DC9', // Player 5 - Purple
    '#FF851B', // Player 6 - Orange
    '#7FDBFF', // Player 7 - Light Blue
    '#01FF70'  // Player 8 - Lime
  ];
  return colors[playerId] || colors[0];
};

export default GameStats; 